import { FormEvent, ReactElement, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { BasicPageLayout } from "../../components/templates/BasicPageLayout";
import { PasswordInput } from "../../components/atoms/AppInputs";
import { useQuery } from "@tanstack/react-query";
import { voislogApi } from "../../utils/apiClients/voislogApi";
import { isApiError } from "../../utils/apiClients/ApiError";
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  Flex,
  Spinner,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import { loginImageInfoAtom } from "../../store";
import { useToastMessage } from "../../hooks/useToastMessage";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ACCOUNT_KEYS } from "../../utils/queryKeyFactory";
import { BannerImage } from "../../components/atoms/BannerImage";
import { useModifiedTranslation } from "../../hooks/useModifiedTranslation";

function NewPasswordForm(props: Readonly<{ resetId: string }>): ReactElement {
  const { i18n } = useTranslation();
  const t = useModifiedTranslation();
  const toastError = useToastMessage();
  const toast = useToast();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmationPassword, setConfirmationPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await voislogApi.PUT(
        "/v2/core/auth/password-reset/verify/{verification_id}",
        {
          params: { path: { verification_id: props.resetId } },
          body: { new_password: newPassword },
        },
      );
      toast({
        status: "success",
        title: t("PasswordReset.NewPassword.success"),
      });
      navigate(`/${i18n.resolvedLanguage}/login`);
    } catch (e) {
      console.error("PasswordReset Error!", e);
      toastError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <VStack spacing={8} mt={8}>
        <PasswordInput
          input={{
            placeholder: t("PasswordReset.NewPassword.input"),
            value: newPassword,
            onChange: (e) => setNewPassword(e.target.value),
          }}
        />
        <PasswordInput
          input={{
            placeholder: t("PasswordReset.NewPassword.confirm"),
            value: confirmationPassword,
            onChange: (e) => setConfirmationPassword(e.target.value),
          }}
        />
        <Button
          type="submit"
          variant="btn_primary"
          isLoading={isSubmitting}
          disabled={!newPassword || newPassword !== confirmationPassword}
          w="full"
        >
          {t("PasswordReset.NewPassword.submit")}
        </Button>
      </VStack>
    </form>
  );
}

export function ResetId(): ReactElement {
  const navigate = useNavigate();
  const t = useModifiedTranslation();
  const toastError = useToastMessage();
  const { resetId } = useParams();

  // ログイン画面に表示する画像のURLをlocalStorageに保持管理用
  const [loginImageInfo] = useAtom(loginImageInfoAtom);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ACCOUNT_KEYS.password.reset(resetId || ""),
    queryFn: async () => {
      try {
        const { data } = await voislogApi.GET(
          "/v2/core/auth/password-reset/verify/{verification_id}",
          { params: { path: { verification_id: resetId! } } },
        );
        return data!;
      } catch (e) {
        if (isApiError(e) && e.status === 404) navigate("/404");
        throw e;
      }
    },
  });

  if (!resetId) {
    return <Navigate to="/404" replace />;
  }

  if (isLoading) {
    return (
      <BasicPageLayout>
        <Center>
          <Spinner />
        </Center>
      </BasicPageLayout>
    );
  }

  if (isError) {
    toastError(error);
  }
  if (!data) {
    return (
      <BasicPageLayout>
        <Alert status="error">
          <AlertIcon />
          Error!
        </Alert>
      </BasicPageLayout>
    );
  }

  const now = dayjs();
  const expiredAt = dayjs(data.expired_at);
  const isExpired = now >= expiredAt;

  return (
    <>
      <Flex justifyContent="center" alignItems="center" pt="3vh" pb="1vh">
        {/* ログイン後にAPIで取得したMIMOSYSのロゴを表示 */}
        {loginImageInfo !== undefined && loginImageInfo !== "" && (
          <BannerImage imageInfo={loginImageInfo} />
        )}
      </Flex>
      <BasicPageLayout title={t("PasswordReset.NewPassword.title")}>
        {isExpired || !data.status ? (
          <Alert status="error">
            <AlertIcon />
            {t("PasswordReset.NewPassword.invalid")}
          </Alert>
        ) : (
          <NewPasswordForm resetId={resetId} />
        )}
      </BasicPageLayout>
    </>
  );
}
